import { isAccountSpecialist, isAdmin, isManager } from "../../../services/utils";
import { MANUAL_ATTEMPT_KEY, REVIEWED_STATUSES, ADMIN_REVIEWABLE_STATUSES, SAVE_ROUTES, SaveRoute } from "./constants";
export { MANUAL_ATTEMPT_KEY, REVIEWED_STATUSES, ADMIN_REVIEWABLE_STATUSES, SAVE_ROUTES };
export type { SaveRoute };

export interface SaveRouteParams {
  selectedCertificationAttempt: string;
  pendingManualReview: boolean;
  assignmentLevelReview: boolean;
  role?: string;
  latestAttemptInfo?: any;
}

/**
 * Determines which save endpoint to call based on the current tab, role, and pending state.
 * - "manual"        → saveManualReview  (on manual tab, or navigated away with pending manual changes)
 * - "adminOverride" → updateAdminReview (admin + assignmentLevelReview + existing review on an attempt tab)
 * - "attemptReview" → saveAttemptReview (manager, or admin with no existing review, or non-assignment-level-review path)
 */
export const getSaveRoute = ({
  selectedCertificationAttempt,
  pendingManualReview,
  assignmentLevelReview,
  role,
  latestAttemptInfo,
}: SaveRouteParams): SaveRoute => {
  if (selectedCertificationAttempt === MANUAL_ATTEMPT_KEY || pendingManualReview) {
    return "manual";
  }
  // If a manual review already exists and the manager is on an attempt tab, the only valid
  // save path is to update the manual review — not to submit a new attempt review.
  if (assignmentLevelReview && isManager(role) && latestAttemptInfo?.hasManualReview) {
    return "manual";
  }
  if (assignmentLevelReview && isAdmin(role) && REVIEWED_STATUSES.includes(latestAttemptInfo?.evaluationStatus ?? "")) {
    return "adminOverride";
  }
  return "attemptReview";
};

export interface ShouldShowSaveButtonParams {
  role?: string;
  selectedCertificationAttempt: string;
  attemptInfo: any;
  latestAttemptInfo: any;
  assignmentLevelReview: boolean;
  showOverallSummary: boolean;
  assignmentDetails: any;
  userInfo: any;
  pendingManualReview: boolean;
}

/**
 * Determines whether the submit button (and editable status selector) should be shown.
 */
export const getShouldShowSaveButton = ({
  role,
  selectedCertificationAttempt,
  attemptInfo,
  latestAttemptInfo,
  assignmentLevelReview,
  showOverallSummary,
  assignmentDetails,
  userInfo,
  pendingManualReview,
}: ShouldShowSaveButtonParams): boolean => {
  // Reviewer cannot review their own certification.
  if (assignmentDetails?.userId === userInfo?.id) {
    return false;
  }

  // If the user changed the verdict on the manual tab but hasn't saved yet, keep the submit
  // button visible regardless of which tab they've navigated to or what the workflow status is.
  // This must run before the workflow-status guard below.
  if (assignmentLevelReview && pendingManualReview) {
    return isAdmin(role) || isManager(role);
  }

  if (showOverallSummary) {
    // Manual tab is only reviewable once a verdict has been set — not while awaiting first review.
    if (selectedCertificationAttempt === MANUAL_ATTEMPT_KEY) {
      return latestAttemptInfo?.evaluationStatus !== "pending_review";
    }
    if (["pending", "in_progress"].includes(latestAttemptInfo?.workflowStatus ?? "")) {
      return false;
    }
    if (isAccountSpecialist(role)) {
      return false;
    }
  }

  // On attempt tabs with assignmentLevelReview:
  // - Admins can save when evaluationStatus is pending_review (normal attempt review),
  //   passed, or failed (admin override). No evaluationStatus means nothing to review yet.
  // - Managers can only submit a normal review when awaiting the first review
  //   (evaluationStatus === "pending_review"). Once a verdict exists, they must use
  //   the manual tab to change the status.
  if (assignmentLevelReview && selectedCertificationAttempt !== MANUAL_ATTEMPT_KEY) {
    if (isAdmin(role)) return ADMIN_REVIEWABLE_STATUSES.includes(latestAttemptInfo?.evaluationStatus ?? "");
    if (isManager(role)) return latestAttemptInfo?.evaluationStatus === "pending_review" || !!latestAttemptInfo?.hasManualReview;
    return false;
  }

  const info = assignmentLevelReview
    ? latestAttemptInfo?.hasManualReview === false && selectedCertificationAttempt === MANUAL_ATTEMPT_KEY
      ? attemptInfo
      : latestAttemptInfo
    : attemptInfo;

  if (info?.review?.length > 0) {
    return false;
  }

  // Special case: manual tab without showOverallSummary is manager-only.
  if (selectedCertificationAttempt === MANUAL_ATTEMPT_KEY && !showOverallSummary) {
    return isManager(role);
  }

  return isAdmin(role) || isManager(role);
};
